import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import styled from "styled-components";
import {
  AlertNotification,
  Button,
  colors,
  Row,
  Col,
  Input,
  Datepicker,
  LabeledIconInput,
  ChevronIcon,
  ConnectedPlusIcon,
  CompletedCheckmarkIcon,
} from "@commonsku/styles";

import { useIdentity } from "../../hooks";
import { createFinalizeAddTax } from "../../actions/tax";
import {
  Bill,
  type PurchaseOrder,
  PurchaseOrderItem,
  ExtraItem,
  Breakdown,
  Cost,
  Tax,
} from "../../types/bills";

import useDraft, {
  DraftBreakdown,
  DraftCost,
  DraftExtraItem,
  DraftItem,
  DraftPurchaseOrderItem,
  DraftUpdater,
} from "./useDraft";
import FormPopup from "../FormPopup";
import UnbilledPurchaseOrdersPopup from "./UnbilledPurchaseOrdersPopup";
import TaxSelect from "../TaxSelect";
import AsyncDivisionSelect from "../AsyncDivisionSelect";
import BillItemTypeSelect from "./BillItemTypeSelect";
import TaxAmountSelect from "../TaxAmountSelect";
import {
  getCurrencySymbol,
  formatMoneyCurrency,
  formatPercent,
  oauth,
} from "../../utils";

const BILL_POPUP_HEIGHT = "90vh";
const BILL_CONFIRM_POPUP_WIDTH = "640px";
const BILL_CONFIRM_POPUP_HEIGHT = "324px";
const BILL_NOTE_PLACEHOLDER_TEXT = "Add a note (optional)";

const BillDetails = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${colors.primary1["65"]};
  border-radius: 8px;
  background: ${colors.white};
`;

const BillDetailsHeader = styled.div`
  && {
    display: flex;
    flex-flow: row nowrap;
    justify-content: space-between;
    align-items: center;
    gap: 2rem;
    height: 3.5rem;
    padding: 0 0.5rem;
    background: ${colors.neutrals.bg1};
    border-radius: 8px 8px 0 0;
    line-height: 1.5rem;
    font-size: 1rem;
    font-weight: 700;
  }
`;

const FormDescription = styled.div`
&& {
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  gap 2rem;
  padding: 0;
  margin: 0
}
`;

const BillItemHeader = styled.div`
  && {
    display: flex;
    flex-flow: row nowrap;
    justify-content: flex-start;
    align-items: center;
    gap: 1rem;
    background: ${colors.neutrals.bg1};
    font-size: 14px;
    font-weight: 600;
    line-height: 1rem;
    height: 3.5rem;
    padding-left: 8px;
  }
`;

const BillItemRow = styled.div`
  && {
    display: flex;
    flex-flow: row nowrap;
    justify-content: flex-start;
    align-items: center;
    gap: 1rem;
    min-height: 3.5rem;
    padding: 0.5rem 0 0.5rem 8px;
  }
`;

const BillItemTaxRow = styled.div`
  && {
    display: flex;
    flex-flow: row nowrap;
    justify-content: flex-start;
    align-items: center;
    gap: 1rem;
    min-height: 3.5rem;
  }
`;

const PurchaseOrder = styled.span`
  font-weight: 700;
`;

const ExtraPurchaseOrderDetail = styled.span`
  font-weight: 400;
`;

const FlexCol = styled(Col)`
  display: flex;
  flex-flow: column;
  justify-content: flex-start;
  align-items: stretch;
  gap: 1rem;

  && {
    margin: 1rem;
  }
`;

const LabelContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  flex-direction: column;
  flex: 2 1 0;

  &&& > :nth-child(2) {
    width: 100%;
  }
`;

const Label = styled.div`
  &&& {
    font-weight: 700;
    padding: 0.5rem 0;
    white-space: nowrap;
  }
`;

const DetailInput = styled(Input)`
  &&& {
    margin: 0;
  }
`;

const ToggleCell = styled.div`
  &&& {
    flex: 0.5 0.5 3%;
    text-align: center;

    & > input {
      margin: 0;
    }
  }
`;

const EmptyCell = styled.div`
  &&& {
    flex: 1.5 1.5 calc(10% + 1rem);
    text-align: center;
    height: 100%;
  }
`;

const CodeCell = styled.div`
  &&& {
    display: flex;
    align-items: center;
    flex: 1 1 7%;
    text-align: left;
    height: 100%;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
`;

const NameCell = styled.div`
  flex: 3 3 21%;
  text-align: left;
  white-space: normal;
  overflow-wrap: break-word;
  word-wrap: break-word;
`;

const QuantityCell = styled.div`
  flex: 1 1 7%;
  text-align: right;
`;

const UnitCostCell = styled.div`
  flex: 2 2 14%;
  text-align: center;
`;

const SubtotalCell = styled.div`
  flex: 1 1 7%;
  text-align: right;
`;

const TaxCell = styled.div`
  flex: 2 2 14%;
  text-align: left;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const TotalCell = styled.div`
  &&& {
    flex: 1 1 7%;
    text-align: right;
  }
`;

const ActionCell = styled.div`
  &&& {
    flex: 2 2 14%;
    text-align: right;
    padding-right: 1rem;
  }
`;

const TaxAmountCell = styled.div`
  flex: 10 10 calc(70% + 8rem);
  text-align: left;
`;

const EmptyActionCell = styled.div`
  &&& {
    flex: 2 2 14%;
    text-align: center;
    height: 100%;
  }
`;

const TextCell = styled.div`
  height: 40px;
  align-content: center;
`;

const ActionLink = styled.a`
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 0.875rem;
  justify-content: flex-end;
`;

const CodeInput = styled(BillItemTypeSelect)`
  &&& {
    text-align: center;
    margin: 0;
    width: 100%;
  }
`;

const DescriptionInput = styled(Input)`
  &&& {
    text-align: left;
    margin: 0;
    width: 100%;
  }
`;

const QuantityInput = styled(Input)`
  &&& {
    text-align: center;
    margin: 0;
    width: 100%;
  }
`;

const CostInput = styled(LabeledIconInput)`
  &&& {
    text-align: center;
    margin: 0;
    width: 100%;
  }
`;

const CustomDatepicker = styled(Datepicker)<{ isReview: boolean }>`
  &&& {
    border-color: ${(props) =>
      props.isReview ? colors.secondary3["60"] : "inherit"};
    color: ${(props) => (props.isReview ? colors.secondary3["60"] : "inherit")};
  }
`;

const getSubtotal = (item) => Number(item.unitCost) * Number(item.quantity);
const getTotal = (item, item_taxes = []) => {
  const subtotal = getSubtotal(item);
  return (
    subtotal +
    (item.taxes ?? item_taxes).reduce(
      (taxes, t) => taxes + Math.round(subtotal * t.percent) / 100,
      0,
    )
  );
};
const getTaxes = (item) =>
  Array.from(
    new Set(
      item.taxes.map((tax) => `${tax.label} ${formatPercent(tax.percent)}`),
    ),
  ).sort();

const getProductSubtotal = (item) =>
  item.breakdowns.reduce((t, b) => t + getSubtotal(b), 0) +
  item.costs.reduce((t, c) => t + getSubtotal(c), 0);

const getProductTotal = (item) => {
  const subtotal = getProductSubtotal(item);
  return (
    subtotal +
    item.taxes.reduce(
      (taxes, t) => taxes + Math.round(subtotal * t.percent) / 100,
      0,
    )
  );
};

const getBillSubtotal = (bill, isBill = false) =>
  bill.orders.reduce(
    (t, o) =>
      t +
      o.items
        .filter((i) => (isBill ? true : i.selected))
        .reduce(
          (tt, i) =>
            tt + ("breakdowns" in i ? getProductSubtotal(i) : getSubtotal(i)),
          0,
        ),
    0,
  );

const getBillTotal = (bill, isBill = false) =>
  bill.orders.reduce(
    (t, o) =>
      t +
      o.items
        .filter((i) => (isBill ? true : i.selected))
        .reduce(
          (tt, i) =>
            tt + ("breakdowns" in i ? getProductTotal(i) : getTotal(i)),
          0,
        ),
    0,
  );

const getBillTax = (bill) => getBillTotal(bill) - getBillSubtotal(bill);

interface ToggledItems {
  [id: string]: boolean;
}

interface TaxMap {
  [taxId: string]: Tax;
}

interface BreakdownRowProps {
  initialBreakdown: Breakdown;
  breakdown: DraftBreakdown;
  item: DraftPurchaseOrderItem;
  currency: string;
  updater: DraftUpdater;
  disabled: boolean;
  isReview: boolean;
  handleSetHasItemChanges?: () => void;
}

function BreakdownRow({
  initialBreakdown,
  breakdown,
  item,
  currency,
  updater,
  disabled,
  isReview,
  handleSetHasItemChanges,
}: BreakdownRowProps) {
  return (
    <BillItemRow
      key={breakdown.refId}
      style={{ background: colors.primary1["10"] }}
    >
      <ToggleCell>&nbsp;</ToggleCell>
      {isReview && (
        <ToggleCell>
          <CompletedCheckmarkIcon />
        </ToggleCell>
      )}
      <CodeCell></CodeCell>
      <NameCell title={breakdown.description}>{breakdown.description}</NameCell>
      <QuantityCell>
        <QuantityInput
          style={isReview ? { borderColor: colors.secondary3["60"] } : {}}
          value={breakdown.quantity}
          disabled={disabled}
          onChange={(e) => {
            updater.setQuantity(breakdown.refId, e.target.value);
            handleSetHasItemChanges?.();
          }}
          onFocus={(e) => e.target.select()}
        />
      </QuantityCell>
      <UnitCostCell>
        <CostInput
          Icon={<span>{getCurrencySymbol("en-US", currency)}</span>}
          iconLabelStyles={
            isReview ? { background: colors.secondary3["60"] } : {}
          }
          style={isReview ? { borderColor: colors.secondary3["60"] } : {}}
          value={breakdown.unitCost}
          disabled={disabled}
          onChange={(e) => {
            updater.setUnitCost(breakdown.refId, e.target.value);
            handleSetHasItemChanges?.();
          }}
          onFocus={(e) => e.target.select()}
          onBlur={(e) => {
            if (Number.isNaN(Number(breakdown.unitCost))) {
              updater.setUnitCost(
                breakdown.refId,
                initialBreakdown?.unitCost != null
                  ? Number(initialBreakdown.unitCost).toFixed(4)
                  : "0.0000",
              );
            }
          }}
        />
      </UnitCostCell>
      <SubtotalCell>
        {formatMoneyCurrency(getSubtotal(breakdown), currency)}
      </SubtotalCell>
      <TaxCell title={getTaxes(item).join(", ")}>
        {getTaxes(item).join(", ")}
      </TaxCell>
      <TotalCell>
        {formatMoneyCurrency(getTotal(breakdown, item.taxes), currency)}
      </TotalCell>
      <EmptyActionCell>&nbsp;</EmptyActionCell>
    </BillItemRow>
  );
}

interface TaxesRowProps {
  zip2tax: boolean;
  item: DraftItem;
  updater: DraftUpdater;
  disabled: boolean;
  isReview?: boolean;
  handleSetHasItemChanges?: () => void;
}

function TaxesRow({
  zip2tax,
  item,
  updater,
  disabled,
  isReview = false,
  handleSetHasItemChanges = () => {},
}: TaxesRowProps) {
  const handleChangeTax = (value: Tax[]) => {
    updater.setTaxes(item.refId, value);
    handleSetHasItemChanges?.();
  };
  const value = item.taxes.map((t) => ({ value: t.taxId, ...t }));
  return (
    <BillItemTaxRow style={{ background: colors.primary1["10"] }}>
      <EmptyCell>&nbsp;</EmptyCell>
      {isReview && <EmptyCell>&nbsp;</EmptyCell>}
      <TaxAmountCell>
        <TaxAmountSelect
          value={value}
          onChange={handleChangeTax}
          isAvalara={false}
          isZip2Tax={zip2tax}
          disabled={disabled}
        />
      </TaxAmountCell>
      <EmptyActionCell>&nbsp;</EmptyActionCell>
    </BillItemTaxRow>
  );
}

interface CostRowProps {
  initialCost: Cost;
  cost: DraftCost;
  item: DraftPurchaseOrderItem;
  currency: string;
  updater: DraftUpdater;
  disabled: boolean;
  isReview: boolean;
  handleSetHasItemChanges?: () => void;
}

function CostRow({
  initialCost,
  cost,
  item,
  currency,
  updater,
  disabled,
  isReview,
  handleSetHasItemChanges,
}: CostRowProps) {
  return (
    <BillItemRow style={{ background: colors.primary1["20"] }}>
      <EmptyCell>&nbsp;</EmptyCell>
      {isReview && <EmptyCell>&nbsp;</EmptyCell>}
      <NameCell title={cost.description}>{cost.description}</NameCell>
      <QuantityCell>
        <QuantityInput
          value={cost.quantity}
          disabled={disabled}
          onChange={(e) => {
            updater.setQuantity(cost.refId, e.target.value);
            handleSetHasItemChanges?.();
          }}
          onFocus={(e) => e.target.select()}
        />
      </QuantityCell>
      <UnitCostCell>
        <CostInput
          Icon={<span>{getCurrencySymbol("en-US", currency)}</span>}
          value={cost.unitCost}
          disabled={disabled}
          onChange={(e) => updater.setUnitCost(cost.refId, e.target.value)}
          onFocus={(e) => e.target.select()}
          onBlur={(e) => {
            if (Number.isNaN(Number(cost.unitCost))) {
              updater.setUnitCost(
                cost.refId,
                initialCost?.unitCost != null
                  ? Number(initialCost.unitCost).toFixed(4)
                  : "0.0000",
              );
              handleSetHasItemChanges?.();
            }
          }}
        />
      </UnitCostCell>
      <SubtotalCell>
        {formatMoneyCurrency(getSubtotal(cost), currency)}
      </SubtotalCell>
      <TaxCell title={getTaxes(item).join(", ")}>
        {getTaxes(item).join(", ")}
      </TaxCell>
      <TotalCell>
        {formatMoneyCurrency(getTotal(cost, item.taxes), currency)}
      </TotalCell>
      <EmptyActionCell>&nbsp;</EmptyActionCell>
    </BillItemRow>
  );
}

interface PurchaseOrderItemRowProps {
  initialItem: PurchaseOrderItem;
  item: DraftPurchaseOrderItem;
  checked: boolean;
  onToggleChecked: () => void;
  currency: string;
  updater: DraftUpdater;
  disabled: boolean;
  zip2tax: boolean;
  isReview: boolean;
  setHasItemChanges?: (hasItemChanges: boolean) => void;
}

function PurchaseOrderItemRow({
  initialItem,
  item,
  checked,
  onToggleChecked,
  currency,
  updater,
  disabled,
  zip2tax,
  isReview,
  setHasItemChanges = () => {},
}: PurchaseOrderItemRowProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [itemChanges, setItemChanges] = useState(false);

  const handleSetHasItemChanges = () => {
    if (!itemChanges) {
      setItemChanges(true);
      setHasItemChanges(true);
    }
  };
  const isOriginalReview = isReview && !itemChanges;

  // If we have a bill created with bill id
  // and this bill item is not in the bill
  // we should not show this row
  // as we don't add new purchase order item into the existing bill

  return (
    <>
      <BillItemRow
        style={{ background: !collapsed ? colors.primary1["10"] : "inherit" }}
      >
        <ToggleCell>
          {disabled ? (
            <>&nbsp;</>
          ) : (
            <input
              type="checkbox"
              checked={checked}
              onChange={onToggleChecked}
            />
          )}
        </ToggleCell>
        {isOriginalReview && (
          <ToggleCell>
            <CompletedCheckmarkIcon />
          </ToggleCell>
        )}
        <CodeCell title={item.code}>{item.code}</CodeCell>
        <NameCell title={item.name}>{item.name}</NameCell>
        <QuantityCell>
          <>&nbsp;</>
        </QuantityCell>
        <UnitCostCell>
          <>&nbsp;</>
        </UnitCostCell>
        <SubtotalCell>
          {formatMoneyCurrency(getProductSubtotal(item), currency)}
        </SubtotalCell>
        <TaxCell title={getTaxes(item).join(", ")}>
          {getTaxes(item).join(", ")}
        </TaxCell>
        <TotalCell>
          {formatMoneyCurrency(getProductTotal(item), currency)}
        </TotalCell>
        <ActionCell>
          {(item.breakdowns.length > 0 || item.costs.length > 0) && (
            <ActionLink onClick={() => setCollapsed(!collapsed)}>
              {collapsed ? (
                <>
                  <ChevronIcon direction="down" />
                  View/Edit
                </>
              ) : (
                <>
                  <ChevronIcon direction="up" />
                  Collapse
                </>
              )}
            </ActionLink>
          )}
        </ActionCell>
      </BillItemRow>
      {!collapsed &&
        item.breakdowns.map((b, index) => (
          <BreakdownRow
            key={b.refId}
            initialBreakdown={initialItem?.breakdowns[index]}
            breakdown={b}
            item={item}
            currency={currency}
            updater={updater}
            disabled={disabled}
            isReview={isOriginalReview}
            handleSetHasItemChanges={handleSetHasItemChanges}
          />
        ))}
      {!collapsed &&
        item.costs.map((c, index) => (
          <CostRow
            key={c.refId}
            initialCost={initialItem?.costs[index]}
            cost={c}
            item={item}
            currency={currency}
            updater={updater}
            disabled={disabled}
            isReview={isOriginalReview}
            handleSetHasItemChanges={handleSetHasItemChanges}
          />
        ))}
      {!collapsed && (
        <TaxesRow
          item={item}
          updater={updater}
          disabled={disabled}
          zip2tax={zip2tax}
          isReview={isOriginalReview}
          handleSetHasItemChanges={handleSetHasItemChanges}
        />
      )}
    </>
  );
}

interface ExtraItemRowProps {
  item: DraftExtraItem;
  checked: boolean;
  onToggleChecked: () => void;
  currency: string;
  updater: DraftUpdater;
  disabled: boolean;
  zip2tax: boolean;
}

function ExtraItemRow({
  item,
  checked,
  onToggleChecked,
  currency,
  updater,
  disabled,
  zip2tax,
}: ExtraItemRowProps) {
  return (
    <>
      <BillItemRow>
        <ToggleCell>
          {disabled ? (
            <>&nbsp;</>
          ) : (
            <input
              type="checkbox"
              checked={checked}
              onChange={onToggleChecked}
            />
          )}
        </ToggleCell>
        <CodeCell>
          <CodeInput
            value={item.billItemTypeId ?? item.code}
            disabled={disabled}
            onChange={(bit) => updater.setCode(item.refId, bit)}
          />
        </CodeCell>
        <NameCell title={item.description}>
          <DescriptionInput
            value={item.description}
            disabled={disabled}
            onChange={(e) => updater.setDescription(item.refId, e.target.value)}
            onBlur={(e) =>
              updater.setDescription(item.refId, item.description.trim())
            }
          />
        </NameCell>
        <QuantityCell>
          <QuantityInput
            value={item.quantity}
            disabled={disabled}
            onChange={(e) => updater.setQuantity(item.refId, e.target.value)}
            onFocus={(e) => e.target.select()}
          />
        </QuantityCell>
        <UnitCostCell>
          <CostInput
            Icon={<span>{getCurrencySymbol("en-US", currency)}</span>}
            value={item.unitCost}
            disabled={disabled}
            onChange={(e) => updater.setUnitCost(item.refId, e.target.value)}
            onFocus={(e) => e.target.select()}
            onBlur={(e) => {
              if (Number.isNaN(Number(item.unitCost))) {
                updater.setUnitCost(item.refId, "0.00");
              }
            }}
          />
        </UnitCostCell>
        <SubtotalCell>
          {formatMoneyCurrency(getSubtotal(item), currency)}
        </SubtotalCell>
        <TaxCell>{getTaxes(item).join(", ")}</TaxCell>
        <TotalCell>
          {formatMoneyCurrency(getTotal(item, item.taxes), currency)}
        </TotalCell>
        <ActionCell>
          <>&nbsp;</>
        </ActionCell>
      </BillItemRow>
      <TaxesRow
        item={item}
        updater={updater}
        disabled={disabled}
        zip2tax={zip2tax}
      />
    </>
  );
}

const DisplayState = {
  EDITING: "EDITING",
  UNBILLED_PURCHASE_ORDERS: "UNBILLED_PURCHASE_ORDERS",
  DONE: "DONE",
} as const;

type DisplayStateType = (typeof DisplayState)[keyof typeof DisplayState];

export default function BillPopup({
  bill,
  onClose,
  onSave,
  isReview = false,
  isDisabled = false,
}: {
  bill: Bill;
  onClose: (saved: boolean) => void;
  onSave?: (params: {
    isNew: boolean;
    isReview: boolean;
    isDirty: boolean;
    bill: Bill;
  }) => Promise<void>;
  isReview?: boolean;
  isDisabled?: boolean;
}) {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(
      createFinalizeAddTax({
        tax_id: bill.tax.taxId,
        label: bill.tax.label,
        percent: bill.tax.percent,
      }),
    );
    bill.orders.forEach((o) =>
      o.items.forEach((i) =>
        i.taxes.forEach((t) =>
          dispatch(
            createFinalizeAddTax({
              tax_id: t.taxId,
              label: t.label,
              percent: t.percent,
            }),
          ),
        ),
      ),
    );
  }, [bill]);

  const taxes = useSelector((state: any) =>
    Object.values(state.entities.taxes).reduce(
      (o: TaxMap, t: any) => ({
        ...o,
        [String(t.tax_id)]: {
          taxId: String(t.tax_id),
          label: String(t.label),
          percent: Number(t.percent),
        },
      }),
      {
        [bill.tax.taxId]: bill.tax,
      } as TaxMap,
    ),
  );

  const [displayState, setDisplayState] = useState<DisplayStateType>(
    DisplayState.EDITING,
  );
  const [unbilledPurchaseOrders, setUnbilledPurchaseOrders] = useState<
    PurchaseOrder[]
  >([]);
  const { draft, isNew, isValid, isEditable, updater, save } = useDraft(bill);
  const disabled = isDisabled || !isEditable;
  const [saved, setSaved] = useState(false);
  const [hasItemChanges, setHasItemChanges] = useState(
    isReview ? false : undefined,
  );
  const identity = useIdentity();

  // Track original purchase orders to detect which ones become unbilled after save
  const originalPurchaseOrders = useMemo<PurchaseOrder[]>(
    () => bill.orders.flatMap((o) => o.purchaseOrders),
    [bill],
  );

  const reviewState = useMemo(() => {
    if (!isReview) {
      return {
        isDirty: false,
        reviewStyles: {
          division: {},
          divisionControl: {},
          divisionDropdownIndicator: {},
          number: {},
          dateBillReview: false,
          dateDueReview: false,
        },
      };
    }

    const isNumberDirty = draft.number !== bill.number;
    const isDivisionDirty = draft.division.id !== bill.division.id;
    const isTaxDirty = draft.tax.taxId !== bill.tax.taxId;
    const isDateBillDirty =
      draft.dateBillDate?.getTime() !== bill.dateBillDate?.getTime();
    const isDateDueDirty = draft.dateDue?.getTime() !== bill.dateDue?.getTime();
    const hasSelectionChanges = draft.orders.some((o) =>
      o.items.some((i) => !i.selected),
    );

    const isDirty =
      isNumberDirty ||
      isDivisionDirty ||
      isTaxDirty ||
      isDateBillDirty ||
      isDateDueDirty ||
      hasItemChanges ||
      hasSelectionChanges;

    const reviewColor = { color: colors.secondary3["60"] };
    const reviewBorderColor = { borderColor: colors.secondary3["60"] };

    return {
      isDirty,
      reviewStyles: {
        division: isDivisionDirty ? {} : reviewColor,
        divisionControl: isDivisionDirty ? {} : reviewBorderColor,
        divisionDropdownIndicator: isDivisionDirty ? {} : reviewColor,
        number: isNumberDirty ? {} : { ...reviewColor, ...reviewBorderColor },
        dateBillReview: !isDateBillDirty,
        dateDueReview: !isDateDueDirty,
      },
    };
  }, [isReview, draft, bill, hasItemChanges]);

  const { isDirty } = reviewState;

  const handleUpdateExchangeRate = useCallback(async () => {
    try {
      await oauth("PUT", `bill/${bill.id}`, { exchange_rate: true });
      updater.updateExchangeRate(true);
    } catch (e) {
      toast.error("Unable to update exchange rate");
      updater.updateExchangeRate(false);
    }
  }, []);

  const handleClose = useCallback(() => onClose(saved), [onClose, saved]);

  const handleBack = useCallback(() => {
    setDisplayState(DisplayState.EDITING);
    setUnbilledPurchaseOrders([]);
  }, []);

  const handleCloseUnbilledPurchaseOrders = useCallback(
    () => setDisplayState(DisplayState.DONE),
    [],
  );

  const handleSave = useCallback(async () => {
    try {
      const savedBill = await save();
      if (onSave) {
        await onSave({
          isNew,
          isReview,
          isDirty,
          bill: savedBill,
        });
        setDisplayState(DisplayState.DONE);
        return;
      }

      const savedPOMap = new Map<string, PurchaseOrder>();
      savedBill.orders.forEach((o) => {
        o.purchaseOrders.forEach((po) => {
          savedPOMap.set(po.id, po);
        });
      });

      // Check all original POs to find which ones are now unbilled
      // This includes POs that had all items deselected (won't be in savedBill)
      const unbilledPurchaseOrders = originalPurchaseOrders.filter(
        (originalPO) => {
          const savedPO = savedPOMap.get(originalPO.id);
          return !savedPO || (savedPO.active && !savedPO.billed);
        },
      );

      setUnbilledPurchaseOrders(unbilledPurchaseOrders);
      if (unbilledPurchaseOrders.length > 0) {
        setDisplayState(DisplayState.UNBILLED_PURCHASE_ORDERS);
      } else {
        setDisplayState(DisplayState.DONE);
      }
      setSaved(true);
    } catch (e) {
      toast.error(
        e?.json?.errors?.[0] ??
          (isNew && !saved ? "Unable to create bill" : "Unable to update bill"),
      );
    }
  }, [
    isNew,
    save,
    saved,
    isDirty,
    isReview,
    handleClose,
    onSave,
    originalPurchaseOrders,
  ]);

  const getBillTitle = () => {
    if (isNew && !saved) {
      if (isReview) {
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <ConnectedPlusIcon color={colors.neutrals["90"]} size="large" />
            Review Bill
          </div>
        );
      }
      return "Create Bill";
    }
    return `Bill ${draft.number}`;
  };

  const hasMultipleDivisions =
    new Set(
      bill.orders.reduce(
        (divisionIds, o) =>
          divisionIds.concat(o.purchaseOrders.map((po) => po.division.id)),
        [],
      ),
    ).size > 1;

  const title = (
    <>
      {getBillTitle()}
      {hasMultipleDivisions && (
        <AlertNotification alertType="warn">
          Includes purchase orders from multiple suppliers
        </AlertNotification>
      )}
    </>
  );

  if (DisplayState.UNBILLED_PURCHASE_ORDERS === displayState) {
    return (
      <UnbilledPurchaseOrdersPopup
        showBack={!saved}
        purchaseOrders={unbilledPurchaseOrders}
        onClose={handleCloseUnbilledPurchaseOrders}
        onBack={handleBack}
      />
    );
  } else if (DisplayState.DONE === displayState) {
    return (
      <FormPopup
        isNew={isNew}
        isEditable={false}
        isValid={isValid}
        onClose={handleClose}
        title={title}
        width={BILL_CONFIRM_POPUP_WIDTH}
        height={BILL_CONFIRM_POPUP_HEIGHT}
        customActions={
          <>
            {!bill.dateExported ? (
              <Button as="a" secondary href="/finance/export-bills">
                View Bills
              </Button>
            ) : (
              <Button as="a" secondary href="/finance/exported-bills">
                View Bills
              </Button>
            )}
            <Button onClick={handleClose}>Close</Button>
          </>
        }
      >
        <AlertNotification alertType="success">
          {isNew ? "Bill created" : "Bill updated"}
        </AlertNotification>
        <span>
          You can find your bill in the{" "}
          <b>{!bill.dateExported ? "Export Bills" : "Exported Bills"}</b> tab of
          Billing
        </span>
      </FormPopup>
    );
  }

  return (
    <FormPopup
      title={title}
      isReview={isReview && !isDirty}
      onClose={handleClose}
      onSave={handleSave}
      isNew={isNew && !saved}
      isValid={isValid}
      isEditable={!disabled}
      height={BILL_POPUP_HEIGHT}
    >
      {isReview && (
        <AlertNotification
          alertType="success"
          learnMore
          href="https://help.commonsku.com/knowledge/automated-billing"
          target="_blank"
        >
          Auto-filled from Connected supplier. Totals match PO, no changes
          needed.
        </AlertNotification>
      )}
      <Row style={{ gap: 16, flexWrap: "nowrap" }}>
        <LabelContainer>
          <Label>Supplier</Label>
          <AsyncDivisionSelect
            valueStyle={reviewState.reviewStyles.division}
            controlStyle={reviewState.reviewStyles.divisionControl}
            dropdownIndicatorStyles={
              reviewState.reviewStyles.divisionDropdownIndicator
            }
            value={draft.division.id}
            onChange={(division) => updater.setDivision(division)}
            isDisabled={disabled}
          />
        </LabelContainer>
        <LabelContainer>
          <Label>Invoice #</Label>
          <DetailInput
            style={reviewState.reviewStyles.number}
            value={draft.number}
            disabled={disabled}
            onChange={(e) => updater.setBillNumber(e.target.value)}
            onBlur={(e) => updater.setBillNumber(draft.number.trim())}
          />
        </LabelContainer>
        <LabelContainer>
          <Label>Invoice Date</Label>
          <CustomDatepicker
            isReview={reviewState.reviewStyles.dateBillReview}
            value={draft.dateBillDate}
            disabled={disabled}
            onChange={(date) => updater.setBillDate(date)}
          />
        </LabelContainer>
        <LabelContainer>
          <Label>Due Date</Label>
          <CustomDatepicker
            isReview={reviewState.reviewStyles.dateDueReview}
            value={draft.dateDue}
            disabled={disabled}
            onChange={(date) => updater.setDueDate(date)}
          />
        </LabelContainer>
        <LabelContainer
          style={
            draft.currency === (bill.orders[0]?.currency ?? draft.currency)
              ? { flex: "1 1 0" }
              : undefined
          }
        >
          <Label>Currency</Label>
          {draft.currency !== (bill.orders[0]?.currency ?? draft.currency) ? (
            <TextCell>
              1 {draft.currency} (Bill) = {bill.exchangeRate}
              {bill.orders[0]?.currency ?? draft.currency} (Invoice) &nbsp;
              {identity.use_margin_currency === 0 && (
                <Button
                  size="tiny"
                  variant="secondary"
                  disabled={draft.exchangeRateUpdated}
                  onClick={handleUpdateExchangeRate}
                >
                  Update
                </Button>
              )}
            </TextCell>
          ) : (
            <TextCell>{draft.currency}</TextCell>
          )}
        </LabelContainer>
        <LabelContainer style={{ flex: "1 1 0" }}>
          <Label>Subtotal</Label>
          <TextCell>
            {formatMoneyCurrency(getBillSubtotal(draft), draft.currency)}
          </TextCell>
        </LabelContainer>
        <LabelContainer>
          <Label>Global Tax</Label>
          <TaxSelect
            value={draft.tax.taxId}
            zip2tax={draft.zip2tax}
            onNewTax={(tax) => {
              taxes[String(tax.tax_id)] = {
                taxId: String(tax.tax_id),
                label: String(tax.label),
                percent: Number(tax.percent),
              };
            }}
            disabled={disabled}
            onChange={(taxId) => updater.setGlobalTax(taxes[taxId])}
          />
        </LabelContainer>
        <LabelContainer style={{ flex: "1 1 0" }}>
          <Label>Total Tax</Label>
          <TextCell>
            {formatMoneyCurrency(getBillTax(draft), draft.currency)}
          </TextCell>
        </LabelContainer>
        <LabelContainer style={{ flex: "1 1 0" }}>
          <Label>Amount Due</Label>
          <TextCell>
            {formatMoneyCurrency(getBillTotal(draft), draft.currency)}
          </TextCell>
        </LabelContainer>
      </Row>
      <Row>
        <Input
          placeholder="Notes"
          value={draft.notes}
          disabled={disabled}
          onChange={(e) => updater.setNotes(e.target.value)}
          onBlur={(e) => updater.setNotes(draft.notes.trim())}
        />
      </Row>
      {draft.orders.map((o, order_index) =>
        o.purchaseOrders
          .map((po, po_index) => (
            <BillDetails key={po.id}>
              <BillDetailsHeader>
                <FormDescription>
                  <PurchaseOrder>
                    PO&nbsp;
                    <a
                      href={`/public_purchase_order.php?id=${po.id}`}
                      target="_blank"
                    >
                      #{po.number}
                    </a>
                  </PurchaseOrder>
                  <ExtraPurchaseOrderDetail>
                    Project&nbsp;
                    <a href={`/project/${o.project.number}`} target="_blank">
                      #{o.project.number}
                    </a>
                  </ExtraPurchaseOrderDetail>
                  <ExtraPurchaseOrderDetail>
                    <a
                      href={`/vendor.php?id=${po.division.supplierId}&division_id=${po.division.id}`}
                      target="_blank"
                    >
                      {po.division.name}
                    </a>
                    {!!po.destination && (
                      <>
                        &nbsp;&gt;&nbsp;
                        <a
                          href={
                            "CLIENT" === po.destination.type
                              ? `/client/${po.destination.id}`
                              : "SUPPLIER" === po.destination.type
                                ? `/vendor.php?id=${po.destination.supplierId}&division_id=${po.destination.id}`
                                : `/distributor.php?id=${po.destination.id}`
                          }
                          target="_blank"
                        >
                          {po.destination.name}
                        </a>
                      </>
                    )}
                  </ExtraPurchaseOrderDetail>
                </FormDescription>
                {!disabled && (
                  <ActionLink
                    onClick={() => {
                      updater.addExtraItem(o.id, po.id);
                      isReview && setHasItemChanges(true);
                    }}
                  >
                    + Add Bill Item
                  </ActionLink>
                )}
              </BillDetailsHeader>
              <BillItemHeader>
                <ToggleCell>&nbsp;</ToggleCell>
                {isReview && <ToggleCell>&nbsp;</ToggleCell>}
                <CodeCell>Code</CodeCell>
                <NameCell>Item Name</NameCell>
                <QuantityCell>QTY</QuantityCell>
                <UnitCostCell>Unit Cost</UnitCostCell>
                <SubtotalCell>Subtotal</SubtotalCell>
                <TaxCell>Tax</TaxCell>
                <TotalCell>Total</TotalCell>
                <ActionCell>&nbsp;</ActionCell>
              </BillItemHeader>
              {o.items
                .filter((i) => i.purchaseOrderId === po.id)
                .map((i, item_index) =>
                  "breakdowns" in i ? (
                    <PurchaseOrderItemRow
                      key={i.refId}
                      initialItem={
                        bill.orders[order_index].items.filter(
                          (i) => i.purchaseOrderId === po.id,
                        )[item_index] as PurchaseOrderItem
                      }
                      item={i}
                      checked={i.selected}
                      onToggleChecked={() =>
                        updater.toggleItemSelected(i.refId)
                      }
                      currency={draft.currency}
                      updater={updater}
                      disabled={disabled}
                      zip2tax={bill.zip2tax}
                      isReview={isReview}
                      setHasItemChanges={
                        isReview ? setHasItemChanges : undefined
                      }
                    />
                  ) : (
                    <ExtraItemRow
                      key={i.refId}
                      item={i}
                      checked={i.selected}
                      onToggleChecked={() =>
                        updater.toggleItemSelected(i.refId)
                      }
                      currency={draft.currency}
                      updater={updater}
                      disabled={disabled}
                      zip2tax={bill.zip2tax}
                    />
                  ),
                )}
            </BillDetails>
          ))
          .concat(
            o.items.filter((i) => !i.purchaseOrderId).length > 0 ||
              o.purchaseOrders.length === 0 ? (
              <BillDetails key={o.id}>
                <BillDetailsHeader>
                  <FormDescription>
                    <PurchaseOrder>
                      Order
                      <a
                        href={`/project/${o.project.number}/sales-order/${o.number}`}
                        target="_blank"
                      >
                        #{o.number}
                      </a>
                    </PurchaseOrder>
                    <ExtraPurchaseOrderDetail>
                      Project
                      <a href={`/project/${o.project.number}`} target="_blank">
                        #{o.project.number}
                      </a>
                    </ExtraPurchaseOrderDetail>
                  </FormDescription>
                  {!disabled && (
                    <ActionLink onClick={() => updater.addExtraItem(o.id)}>
                      + Add Bill Item
                    </ActionLink>
                  )}
                </BillDetailsHeader>
                {o.items
                  .filter((i) => !i.purchaseOrderId)
                  .map((i: DraftExtraItem) => (
                    <ExtraItemRow
                      key={i.refId}
                      item={i}
                      checked={i.selected}
                      onToggleChecked={() =>
                        updater.toggleItemSelected(i.refId)
                      }
                      currency={draft.currency}
                      updater={updater}
                      disabled={disabled}
                      zip2tax={bill.zip2tax}
                    />
                  ))}
              </BillDetails>
            ) : (
              []
            ),
          ),
      )}
    </FormPopup>
  );
}
